import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { Vehicle, VehicleHistory, Alert, FilterOptions } from '../types'
import { vehicleService } from '../services/vehicleService'
import { useAuth } from './AuthContext'
import { useSettings } from './SettingsContext'

interface VehicleState {
  vehicles: Vehicle[]
  selectedVehicle: Vehicle | null
  vehicleHistory: VehicleHistory[]
  alerts: Alert[]
  loading: boolean
  error: string | null
  filters: FilterOptions
  realTimeEnabled: boolean
}

type VehicleAction =
  | { type: 'SET_VEHICLES'; payload: Vehicle[] }
  | { type: 'SELECT_VEHICLE'; payload: Vehicle | null }
  | { type: 'SET_VEHICLE_HISTORY'; payload: VehicleHistory[] }
  | { type: 'SET_ALERTS'; payload: Alert[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FILTERS'; payload: FilterOptions }
  | { type: 'TOGGLE_REAL_TIME' }
  | { type: 'UPDATE_VEHICLE'; payload: Vehicle }
  | { type: 'ADD_ALERT'; payload: Alert }

const initialState: VehicleState = {
  vehicles: [],
  selectedVehicle: null,
  vehicleHistory: [],
  alerts: [],
  loading: false,
  error: null,
  filters: {},
  realTimeEnabled: true
}

function vehicleReducer(state: VehicleState, action: VehicleAction): VehicleState {
  switch (action.type) {
    case 'SET_VEHICLES':
      return {
        ...state,
        vehicles: action.payload,
        // Keep the selected vehicle attached to the refreshed API row. The old
        // behavior left its position frozen while the fleet list kept moving.
        selectedVehicle: state.selectedVehicle
          ? action.payload.find(vehicle => vehicle.id === state.selectedVehicle?.id) || state.selectedVehicle
          : null,
        loading: false
      }
    case 'SELECT_VEHICLE':
      return { ...state, selectedVehicle: action.payload }
    case 'SET_VEHICLE_HISTORY':
      return { ...state, vehicleHistory: action.payload }
    case 'SET_ALERTS':
      return { ...state, alerts: action.payload }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    case 'SET_FILTERS':
      return { ...state, filters: action.payload }
    case 'TOGGLE_REAL_TIME':
      return { ...state, realTimeEnabled: !state.realTimeEnabled }
    case 'UPDATE_VEHICLE':
      return {
        ...state,
        vehicles: state.vehicles.map(v => 
          v.id === action.payload.id ? action.payload : v
        ),
        selectedVehicle: state.selectedVehicle?.id === action.payload.id 
          ? action.payload 
          : state.selectedVehicle
      }
    case 'ADD_ALERT':
      return { ...state, alerts: [action.payload, ...state.alerts] }
    default:
      return state
  }
}

interface VehicleContextType {
  state: VehicleState
  dispatch: React.Dispatch<VehicleAction>
  selectVehicle: (vehicle: Vehicle | null) => void
  loadVehicles: () => Promise<void>
  loadVehicleHistory: (vehicleId: string, startDate?: Date, endDate?: Date) => Promise<void>
  updateFilters: (filters: FilterOptions) => void
  toggleRealTime: () => void
}

const VehicleContext = createContext<VehicleContextType | undefined>(undefined)

export function VehicleProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(vehicleReducer, initialState)
  const { isAuthenticated } = useAuth()
  const { get: getSetting } = useSettings()
  const dashboardRefreshSeconds = Number(getSetting('refresh.dashboard_seconds', 30))
  const mapRefreshSeconds = Number(getSetting('refresh.map_seconds', 30))
  const refreshSeconds = Math.min(dashboardRefreshSeconds, mapRefreshSeconds)

  const selectVehicle = (vehicle: Vehicle | null) => {
    dispatch({ type: 'SELECT_VEHICLE', payload: vehicle })
    if (vehicle) {
      loadVehicleHistory(vehicle.id)
    }
  }

  const loadVehicles = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const vehicles = await vehicleService.getVehicles(state.filters)
      dispatch({ type: 'SET_VEHICLES', payload: vehicles })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load vehicles' })
    }
  }

  const loadVehicleHistory = async (vehicleId: string, startDate?: Date, endDate?: Date) => {
    try {
      const history = await vehicleService.getVehicleHistory(vehicleId, startDate, endDate)
      dispatch({ type: 'SET_VEHICLE_HISTORY', payload: history })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load vehicle history' })
    }
  }

  const updateFilters = (filters: FilterOptions) => {
    dispatch({ type: 'SET_FILTERS', payload: filters })
    loadVehicles()
  }

  const toggleRealTime = () => {
    dispatch({ type: 'TOGGLE_REAL_TIME' })
  }

  // Real-time updates (only while logged in — unauthenticated calls 401)
  useEffect(() => {
    if (!state.realTimeEnabled || !isAuthenticated) return

    const interval = setInterval(async () => {
      try {
        const vehicles = await vehicleService.getVehicles(state.filters)
        dispatch({ type: 'SET_VEHICLES', payload: vehicles })
      } catch (error) {
        console.error('Real-time update failed:', error)
      }
    }, Math.max(10, Number(refreshSeconds)) * 1000)

    return () => clearInterval(interval)
  }, [state.realTimeEnabled, state.filters, isAuthenticated, refreshSeconds])

  // Load data as soon as the user is authenticated (covers both an existing
  // session on page load and a fresh login, without waiting for the first
  // 30s refresh tick)
  useEffect(() => {
    if (isAuthenticated) loadVehicles()
  }, [isAuthenticated])

  const value: VehicleContextType = {
    state,
    dispatch,
    selectVehicle,
    loadVehicles,
    loadVehicleHistory,
    updateFilters,
    toggleRealTime
  }

  return (
    <VehicleContext.Provider value={value}>
      {children}
    </VehicleContext.Provider>
  )
}

export function useVehicles() {
  const context = useContext(VehicleContext)
  if (context === undefined) {
    throw new Error('useVehicles must be used within a VehicleProvider')
  }
  return context
}
